/**
 * Minimal, dependency-free carousel controls for the Shop by Brand track.
 * Native CSS scroll-snap already handles the actual scrolling/swiping on
 * every device; this only adds optional desktop arrow buttons that scroll
 * the existing track by one page and reflect start/end state. No autoplay,
 * no looping - scrolling simply clamps at the track's natural boundaries.
 */
class ShopByBrandCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-shop-by-brand-track]');
    this.prevButton = this.querySelector('[data-shop-by-brand-prev]');
    this.nextButton = this.querySelector('[data-shop-by-brand-next]');

    if (!this.track || !this.prevButton || !this.nextButton) return;

    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.ticking = false;

    this.onPrevClick = () => this.scrollByPage(-1);
    this.onNextClick = () => this.scrollByPage(1);
    this.onTrackScroll = () => this.queueUpdateControls();
    this.onResize = () => this.queueUpdateControls();

    this.prevButton.addEventListener('click', this.onPrevClick);
    this.nextButton.addEventListener('click', this.onNextClick);
    this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    window.addEventListener('load', this.onResize);

    this.updateControls();
  }

  disconnectedCallback() {
    if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrevClick);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNextClick);
    if (this.track) this.track.removeEventListener('scroll', this.onTrackScroll);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('load', this.onResize);
  }

  scrollByPage(direction) {
    const amount = this.track.clientWidth * 0.9 * direction;
    this.track.scrollBy({
      left: amount,
      behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
    });
  }

  queueUpdateControls() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.updateControls();
      this.ticking = false;
    });
  }

  updateControls() {
    const track = this.track;
    const hasOverflow = track.scrollWidth - track.clientWidth > 1;

    this.prevButton.hidden = !hasOverflow;
    this.nextButton.hidden = !hasOverflow;

    if (!hasOverflow) return;

    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;

    this.prevButton.disabled = atStart;
    this.nextButton.disabled = atEnd;
  }
}

customElements.define('shop-by-brand-carousel', ShopByBrandCarousel);
